import { Heart, ScanSearch } from 'lucide-react';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { PageHeader } from '@/components/common/page-header';
import { ErrorState } from '@/components/common/states';
import { NewJobDialog } from '@/components/indexing/new-job-dialog';
import { MovieGrid } from '@/components/movie/movie-grid';
import { PersonPhoto } from '@/components/movie/person-photo';
import { Button } from '@/components/ui/button';
import { useFavouritePerson, usePerson } from '@/hooks/use-movies';
import { cn } from '@/lib/utils';

export function PersonPage() {
  const personId = Number(useParams().personId);
  const person = usePerson(personId);
  const favourite = useFavouritePerson(personId);
  const [indexing, setIndexing] = useState(false);
  if (person.isError) {
    return <ErrorState error={person.error} />;
  }
  const found = person.data?.person;
  // Her page on a source, as a category to index; the first source that lists her.
  const category = found?.categories?.[0];
  const isFavourite = found?.favourite === true;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-4">
        {found === undefined ? null : <PersonPhoto name={found.name} photo={found.photo} className="size-20 text-2xl" />}
        <PageHeader
          className="min-w-0 flex-1"
          title={found?.name ?? '…'}
          description={
            found === undefined
              ? undefined
              : `${found.movieCount.toLocaleString()} in the library${category?.movieCount === undefined ? '' : ` · ${category.movieCount.toLocaleString()} on ${category.sourceId}`}`
          }
          actions={
            found === undefined ? undefined : (
              <>
                <Button variant="outline" onClick={() => favourite.mutate(!isFavourite)} disabled={favourite.isPending} aria-pressed={isFavourite}>
                  <Heart className={cn(isFavourite && 'fill-primary text-primary')} />
                  Favourite
                </Button>
                <Button onClick={() => setIndexing(true)} disabled={category === undefined} title={category === undefined ? undefined : `Index her movies from ${category.sourceId}`}>
                  <ScanSearch />
                  Index her movies
                </Button>
              </>
            )
          }
        />
      </div>
      {found !== undefined && category === undefined ? (
        <p className="text-sm text-muted-foreground">No source lists a page of hers to index yet. Reading the categories (under Indexing) finds it if the source has one.</p>
      ) : null}
      <MovieGrid request={{ personId }} empty={{ title: 'No movies indexed with this person yet' }} />
      <NewJobDialog open={indexing} onOpenChange={setIndexing} category={category} />
    </div>
  );
}
